import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "randmarcomps";

interface TablePaginationBarProps {
  page: number;
  rowsPerPage: number;
  totalRows: number;
  rowsPerPageOptions: readonly number[];
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  className?: string;
}

export function TablePaginationBar({
  page,
  rowsPerPage,
  totalRows,
  rowsPerPageOptions,
  onPageChange,
  onRowsPerPageChange,
  className,
}: TablePaginationBarProps) {
  const safeRowsPerPage = Math.max(1, rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(totalRows / safeRowsPerPage));
  const clampedPage = Math.min(Math.max(page, 0), totalPages - 1);

  const startIndex = totalRows === 0 ? 0 : clampedPage * safeRowsPerPage + 1;
  const endIndex = Math.min(totalRows, (clampedPage + 1) * safeRowsPerPage);

  return (
    <div
      className={[
        "mt-4 flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <p className="text-sm text-slate-500">
        Showing {startIndex}–{endIndex} of {totalRows}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Rows
          </span>
          <Select
            value={String(safeRowsPerPage)}
            onValueChange={(value) => {
              const next = Number.parseInt(value, 10);
              onRowsPerPageChange(next);
              onPageChange(0);
            }}
          >
            <SelectTrigger className="h-10 w-[110px] rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {rowsPerPageOptions.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.max(0, clampedPage - 1))}
            disabled={clampedPage === 0}
            className="rounded-xl"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Prev
          </Button>
          <p className="text-sm font-medium text-slate-700">
            Page {clampedPage + 1} of {totalPages}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.min(totalPages - 1, clampedPage + 1))}
            disabled={clampedPage >= totalPages - 1}
            className="rounded-xl"
          >
            Next
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}
